import type { MailRibbonConfigStore, TabNameType } from '../types/mailRibbonConfigStore';
import { computed, type IComputedValue } from 'mobx';
import { defaultConfig } from './defaultConfig';
import type { TabScalingAndLayoutConfig } from '../types/tabScalingAndLayoutConfig';
import { getRibbonSectionContents } from './getRibbonSectionContents';
import { type MailRibbonGroupId } from 'owa-ribbon-ids/lib/mailRibbonId';
import { type FormatControlId } from 'owa-ribbon-ids/lib/formatControlId';
import { type ActionControlId } from 'owa-ribbon-ids/lib/actionControlId';

/**
 * This is the config for the reordering of Authoring owned tabs
 * The tabs are:
 * - Message
 * - Insert
 * - Format text
 * - Options
 * - Table
 * - Picture Format
 *
 * The config is used to set the layout and staticGroupIdOrdering for each tab
 */

type PartialRibbonConfiguration = {
    [linekey in 'multiline' | 'singleline']: {
        [key in TabNameType]?: {
            layout?: TabScalingAndLayoutConfig['layout'];
            staticGroupIdOrdering?: TabScalingAndLayoutConfig['staticGroupIdOrdering'];
        };
    };
};

export const newConfig: PartialRibbonConfiguration = {
    multiline: {
        messageTab: {
            layout: [
                getRibbonSectionContents(163, [6012, 6009, 6010, 6029, 6014, 6015]),
                getRibbonSectionContents(
                    157,
                    [6027, 6028, 6024, 6030, 6001, 6002, 6003, 6004, 6026, 6025, 6022, 6023]
                ),
                getRibbonSectionContents(
                    135,
                    [6005, 6006, 6008, 6007, 6073, 6016, 6071, 6075, 6020, 6021]
                ),
                getRibbonSectionContents(115, [7049, 7050]),
                getRibbonSectionContents(161, [620, 7025, 506, 638, 7026, 7011, 639, 637]),
                getRibbonSectionContents(173, [7000]),
                getRibbonSectionContents(180, [7023]),
                getRibbonSectionContents(187, [7029]),
                getRibbonSectionContents(172, [7001]),
                getRibbonSectionContents(177, [7003]),
                getRibbonSectionContents(159, [529, 7016, 7021]),
                getRibbonSectionContents(160, [7005, 7006, 564, 7012]),
                getRibbonSectionContents(199, [7036]),
                getRibbonSectionContents(162, [7004, 6072, 7010, 7040]),
            ],
            staticGroupIdOrdering: [
                163, 157, 135, 115, 161, 173, 180, 187, 172, 177, 159, 160, 171, 162,
            ],
        },
        insertTab: {
            layout: [
                getRibbonSectionContents(111, [620, 7025, 506, 7026, 638, 639, 7042, 637, 7054]),
                getRibbonSectionContents(169, [7000]),
                getRibbonSectionContents(165, [529, 7016, 7021]),
                getRibbonSectionContents(166, [7011]),
            ],
            staticGroupIdOrdering: [111, 165, 169, 166],
        },
        formatTab: {
            layout: [
                getRibbonSectionContents(102, [6012, 6009, 6010, 6029, 6014, 6015]),
                getRibbonSectionContents(
                    134,
                    [6027, 6028, 6024, 6030, 6001, 6002, 6003, 6004, 6026, 6025, 6022, 6023]
                ),
                getRibbonSectionContents(
                    135,
                    [6005, 6006, 6008, 6007, 6073, 6016, 6071, 6075, 6020, 6021]
                ),
            ],
            staticGroupIdOrdering: [102, 134, 135],
        },
        optionsTab: {
            layout: [
                getRibbonSectionContents(172, [7001, 7004]),
                getRibbonSectionContents(124, [7013]),
                getRibbonSectionContents(175, [7034, 7020, 7019, 7009, 7039]),
                getRibbonSectionContents(176, [7015, 7014, 7031]),
                getRibbonSectionContents(167, [7005, 7006, 564, 7012]),
                getRibbonSectionContents(199, [7036]),
                getRibbonSectionContents(174, [7002]),
                getRibbonSectionContents(123, [560, 7035, 7040]),
                getRibbonSectionContents(162, [6072, 7010]),
            ],
            staticGroupIdOrdering: [172, 124, 167, 123, 162],
        },
        tableFormatTab: {
            layout: [
                getRibbonSectionContents(147, [6037]),
                getRibbonSectionContents(148, [6046]),
                getRibbonSectionContents(196, [6047, 6098, 6097]),
                getRibbonSectionContents(150, [6042, 6058, 6059, 6060, 6061]),
                getRibbonSectionContents(149, [6049, 6054]),
                getRibbonSectionContents(151, [6087]),
                getRibbonSectionContents(201, [6113]),
            ],
            staticGroupIdOrdering: [147, 148, 196, 150, 149],
        },
        pictureTab: {
            layout: [
                getRibbonSectionContents(188, [6079, 6080]),
                getRibbonSectionContents(190, [6083, 6081, 6082]),
                getRibbonSectionContents(191, [6084]),
                getRibbonSectionContents(189, [6085]),
            ],
            staticGroupIdOrdering: [188, 190, 191, 189],
        },
    },
    singleline: {
        insertTab: {
            layout: [
                getRibbonSectionContents(111, [620, 7025, 506, 7026, 638, 639, 7042, 637, 7054]),
                getRibbonSectionContents(169, [7000]),
                getRibbonSectionContents(165, [529, 7016, 7021]),
                getRibbonSectionContents(166, [7011]),
            ],
            staticGroupIdOrdering: [111, 165, 169, 166],
        },
        formatTab: {
            layout: [
                getRibbonSectionContents(102, [6013, 6012, 6029]),
                getRibbonSectionContents(
                    134,
                    [6027, 6028, 6001, 6002, 6003, 6004, 6026, 6025, 6030, 6024, 6023, 6022]
                ),
                getRibbonSectionContents(179, [6073, 6016]),
                getRibbonSectionContents(135, [6005, 6006, 6008, 6007, 6071, 6075, 6020, 6021]),
            ],
            staticGroupIdOrdering: [102, 134, 135],
        },
        messageTab: {
            layout: [
                getRibbonSectionContents(163, [6013, 6012, 6029]),
                getRibbonSectionContents(
                    157,
                    [
                        6027, 6028, 6001, 6002, 6003, 6004, 6026, 6025, 6073, 6016, 6005, 6006,
                        6008, 6007, 6071, 6075, 6030, 6022, 6023,
                    ],
                    true /* hasOwnOverflow*/
                ),
                getRibbonSectionContents(115, [7049, 7050]),

                getRibbonSectionContents(158, [620, 638, 7025, 506, 7026]),
                getRibbonSectionContents(180, [7023]),

                getRibbonSectionContents(169, [7000]),
                getRibbonSectionContents(187, [7029]),
                getRibbonSectionContents(172, [7001]),
                getRibbonSectionContents(177, [7003]),
                getRibbonSectionContents(159, [529, 7016, 7021]),
                getRibbonSectionContents(160, [7005, 7006, 564, 7012]),
                getRibbonSectionContents(135, [6020, 6021]),
                getRibbonSectionContents(161, [639, 637]),
                getRibbonSectionContents(162, [7004, 7036, 7035, 7011, 7010, 7040]),
            ],
            staticGroupIdOrdering: [163, 157, 115, 158, 159, 187, 160, 161, 162],
        },
        tableFormatTab: {
            layout: [
                getRibbonSectionContents(147, [6037]),
                getRibbonSectionContents(148, [6046]),
                getRibbonSectionContents(196, [6047, 6098, 6097]),
                getRibbonSectionContents(150, [6042, 6058, 6059, 6060, 6061]),
                getRibbonSectionContents(149, [6049, 6054]),
                getRibbonSectionContents(151, [6087]),
                getRibbonSectionContents(201, [6113]),
            ],
            staticGroupIdOrdering: [147, 148, 196, 150, 149],
        },
        optionsTab: {
            layout: [
                getRibbonSectionContents(172, [7001, 7004]),
                getRibbonSectionContents(124, [7013, 7034]),
                getRibbonSectionContents(175, [7057]),
                getRibbonSectionContents(176, [7058]),
                getRibbonSectionContents(167, [7005, 7006, 564, 7012]),
                getRibbonSectionContents(199, [7036]),
                getRibbonSectionContents(168, [7002, 560, 7035, 6072, 7010, 7040]),
            ],
            staticGroupIdOrdering: [172, 124, 175, 176, 167],
        },
        pictureTab: {
            layout: [
                getRibbonSectionContents(188, [6079, 6080]),
                getRibbonSectionContents(190, [6083, 6081, 6082]),
                getRibbonSectionContents(191, [6084]),
                getRibbonSectionContents(189, [6085]),
            ],
            staticGroupIdOrdering: [188, 190, 191, 189],
        },
    },
};

export const mergedConfig: IComputedValue<MailRibbonConfigStore> = computed(() =>
    combineConfigStores(defaultConfig, newConfig)
);

export function combineConfigStores(
    oldConfigStore: MailRibbonConfigStore,
    newConfigStore: PartialRibbonConfiguration
): MailRibbonConfigStore {
    const mergedStore = {
        ...oldConfigStore,
    };

    for (const multilineKey of Object.keys(newConfigStore.multiline)) {
        const multilineTab = multilineKey as TabNameType;
        mergedStore.multiline[multilineTab] = {
            ...oldConfigStore.multiline[multilineTab],
            ...newConfigStore.multiline[multilineTab],
        };
    }

    for (const singlelineKey of Object.keys(newConfigStore.singleline)) {
        const singlelineTab = singlelineKey as TabNameType;
        mergedStore.singleline[singlelineTab] = {
            ...oldConfigStore.singleline[singlelineTab],
            ...newConfigStore.singleline[singlelineTab],
        };
    }

    return mergedStore;
}
